import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AnalyticsMetricCard, AnalyticsPageHero } from './analyticsSurface';
import { requestProjectRoute, isTrainTransitStep } from './googleMapsRouting';
import { API_BASE_URL, fetchJson } from './api';
import UKProjectMap from './UKProjectMap';

const OFFICE_MAP_SETTINGS_UPDATED_EVENT = 'monsurv:office-map-settings-updated';
const GOOGLE_MAPS_API_KEY = 'AIzaSyB8aHKsvXwcOfduoR86RY1jNw1nAK_xn34';
const USER_PREFS_CACHE_KEY = 'monsurv_user_preferences_v1';
const CLIENTS_KEY = 'monsurv_clients';
const FEE_PROPOSAL_SERVICE_LINE_OPTIONS = [
    { value: 'monitoring_surveyor_full_report', label: 'Monitoring Surveyor - Full Report' },
    { value: 'monitoring_surveyor_dashboard', label: 'Monitoring Surveyor - Dashboard' }
];

function getTodayDateInputValue() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function createEmptyForm() {
    return {
        client_name: '',
        client_point_of_contact: '',
        site_address: '',
        contract_value: '',
        office_id: '',
        service_line: '',
        travel_mode: 'driving',
        proposed_initial_report_fee: '',
        proposed_interim_report_fee: '',
        issue_date: getTodayDateInputValue(),
        road_distance: '',
        travel_time: '',
        departure_station: '',
        arrival_station: ''
    };
}

function getAuthHeaderOnly() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function normalizeOffice(office, index) {
    const value = office && typeof office === 'object' ? office : {};
    return {
        id: String(value.id || `office_${index + 1}`).trim() || `office_${index + 1}`,
        office_location: String(value.office_location || '').trim(),
        address: String(value.address || '').trim(),
        pin_color: String(value.pin_color || '#0f3b68').trim() || '#0f3b68'
    };
}

function normalizeOfficeMapSettings(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const offices = (Array.isArray(value.offices) ? value.offices : [])
        .map((office, index) => normalizeOffice(office, index))
        .filter((office) => office.office_location || office.address);

    return {
        offices,
        primary_office_id: String(value.primary_office_id || offices[0]?.id || '').trim(),
        brand_upload_url: String(value.brand_upload_url || '').trim()
    };
}

function formatCurrencyInput(value) {
    const numeric = Number(String(value || '').replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(numeric)) return 'GBP 0';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(numeric);
}

function sanitizeCurrencyValue(value) {
    const cleaned = String(value || '').replace(/[^\d.]/g, '');
    const [whole, ...rest] = cleaned.split('.');
    return rest.length ? `${whole}.${rest.join('')}` : whole;
}

function formatCurrencyFieldValue(value) {
    const cleaned = sanitizeCurrencyValue(value);
    if (!cleaned) return '';
    const numeric = Number(cleaned);
    if (!Number.isFinite(numeric)) return '';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numeric);
}

function formatNumberFieldValue(value) {
    const cleaned = sanitizeCurrencyValue(value);
    if (!cleaned) return '';
    const numeric = Number(cleaned);
    if (!Number.isFinite(numeric)) return '';
    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(numeric);
}

function parseUserPreferencesFromStorage() {
    try {
        const raw = localStorage.getItem(USER_PREFS_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function readReferenceSeed(preferences) {
    const seed = String(preferences?.reports?.fee_proposal_reference_seed || '').replace(/\D/g, '');
    return seed || '1000';
}

function loadClientsFromStorage() {
    try {
        const raw = localStorage.getItem(CLIENTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function formatProposalReference(referenceNumber) {
    const numeric = String(referenceNumber || '').replace(/\D/g, '');
    const padded = numeric ? numeric.padStart(4, '0') : '1000';
    return `FP-${padded}`;
}

function parseProposalReferenceNumber(reference) {
    const numeric = Number(String(reference || '').replace(/\D/g, ''));
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

function parseContentDispositionFileName(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }

    const simpleMatch = raw.match(/filename="?([^";]+)"?/i);
    return simpleMatch?.[1] ? String(simpleMatch[1]).trim() : '';
}

function triggerBrowserDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'fee_proposal.docx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function clearTravelFields(current) {
    return {
        ...current,
        road_distance: '',
        travel_time: '',
        departure_station: '',
        arrival_station: ''
    };
}

export async function calculateTravelDetails({ originAddress, destinationAddress, travelMode }) {
    const route = await requestProjectRoute({
        origin: originAddress,
        destination: destinationAddress,
        travelMode,
        legacyKey: GOOGLE_MAPS_API_KEY
    });

    if (travelMode === 'train') {
        return {
            road_distance: '',
            travel_time: route.durationText,
            departure_station: route.departureStation,
            arrival_station: route.arrivalStation,
            transitSteps: route.transitSteps
        };
    }

    return {
        road_distance: route.distanceText,
        travel_time: route.durationText,
        departure_station: '',
        arrival_station: '',
        transitSteps: []
    };
}

export default function FeeProposals({ authUser }) {
    const [form, setForm] = useState(() => createEmptyForm());
    const [officeSettings, setOfficeSettings] = useState({ offices: [], primary_office_id: '', brand_upload_url: '' });
    const [referenceSeed, setReferenceSeed] = useState(() => readReferenceSeed(parseUserPreferencesFromStorage()));
    const [clientOptions, setClientOptions] = useState(() => loadClientsFromStorage());
    const [distanceStatus, setDistanceStatus] = useState('');
    const [debouncedSiteAddress, setDebouncedSiteAddress] = useState('');
    const [generateError, setGenerateError] = useState('');
    const [generateMessage, setGenerateMessage] = useState('');
    const [generating, setGenerating] = useState(false);
    const [registerRows, setRegisterRows] = useState([]);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState('');
    const [downloadingId, setDownloadingId] = useState('');
    const [issuingId, setIssuingId] = useState('');
    const [issueDialogRow, setIssueDialogRow] = useState(null);
    const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
    const [rowMenuRow, setRowMenuRow] = useState(null);
    const [editDialogRow, setEditDialogRow] = useState(null);
    const [editForm, setEditForm] = useState({ office_id: '', proposed_initial_report_fee: '', proposed_interim_report_fee: '' });
    const [editBusy, setEditBusy] = useState(false);
    const [editError, setEditError] = useState('');
    const [deleteDialogRow, setDeleteDialogRow] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const displayName = String(authUser?.display_name || authUser?.full_name || authUser?.name || 'Team').trim();

    async function loadFeeProposalRegister() {
        setRegisterError('');
        setRegisterLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/fee-proposals`, {
                method: 'GET',
                headers: getAuthHeaderOnly()
            });
            setRegisterRows(Array.isArray(json?.items) ? json.items : []);
        } catch (e) {
            setRegisterError(e?.message || 'Failed to load fee proposals');
        } finally {
            setRegisterLoading(false);
        }
    }

    useEffect(() => {
        let cancelled = false;

        async function loadOfficeSettings() {
            try {
                const resp = await fetch(`${API_BASE_URL}/settings/office-map`, {
                    method: 'GET',
                    headers: getAuthHeaderOnly()
                });
                const json = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(json?.error || 'Failed to load office map settings');
                if (!cancelled) {
                    const nextSettings = normalizeOfficeMapSettings(json?.settings || null);
                    setOfficeSettings(nextSettings);
                    setForm((current) => ({
                        ...current,
                        office_id: current.office_id || nextSettings.primary_office_id || nextSettings.offices[0]?.id || ''
                    }));
                }
            } catch {
                if (!cancelled) setOfficeSettings({ offices: [], primary_office_id: '', brand_upload_url: '' });
            }
        }

        loadOfficeSettings();

        function handleOfficeSettingsUpdated(event) {
            if (cancelled) return;
            const nextSettings = normalizeOfficeMapSettings(event?.detail?.settings || null);
            setOfficeSettings(nextSettings);
            setForm((current) => ({
                ...current,
                office_id: MANAGEMENT_DEFAULT_OFFICE(current.office_id, nextSettings)
            }));
        }

        window.addEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeSettingsUpdated);
        void loadFeeProposalRegister();
        return () => {
            cancelled = true;
            window.removeEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeSettingsUpdated);
        };
    }, []);

    const offices = officeSettings.offices;
    const selectedOffice = useMemo(
        () => offices.find((office) => office.id === form.office_id) || offices[0] || null,
        [offices, form.office_id]
    );
    const mapProjects = useMemo(() => {
        const address = String(debouncedSiteAddress || '').trim();
        if (!address) return [];
        return [{
            name: String(form.client_name || 'Project site').trim() || 'Project site',
            address
        }];
    }, [debouncedSiteAddress, form.client_name]);
    const proposalReference = useMemo(() => {
        const baseReference = parseProposalReferenceNumber(referenceSeed) ?? 1000;
        const maxRegisterReference = registerRows.reduce((maxValue, row) => {
            const numeric = parseProposalReferenceNumber(row?.proposal_reference);
            return numeric !== null && numeric > maxValue ? numeric : maxValue;
        }, baseReference - 1);
        return formatProposalReference(Math.max(baseReference, maxRegisterReference + 1));
    }, [referenceSeed, registerRows]);
    const clientCompanyNames = useMemo(() => (
        Array.from(new Set(clientOptions
            .map((client) => String(client?.companyName || client?.company_name || client?.name || '').trim())
            .filter(Boolean)))
            .sort((a, b) => a.localeCompare(b))
    ), [clientOptions]);
    const selectedClient = useMemo(
        () => clientOptions.find((client) => String(client?.companyName || client?.company_name || client?.name || '').trim() === String(form.client_name || '').trim()) || null,
        [clientOptions, form.client_name]
    );
    const travelDetailItems = useMemo(() => {
        if (form.travel_mode === 'train') {
            return [
                { label: 'Departure Station', value: form.departure_station || 'Unavailable until a train route is found.' },
                { label: 'Arrival Station', value: form.arrival_station || 'Unavailable until a train route is found.' },
                { label: 'Travel Time', value: form.travel_time || 'Unavailable until a train route is found.' }
            ];
        }

        return [
            { label: 'Miles', value: form.road_distance || 'Unavailable until a driving route is found.' },
            { label: 'Travel Time', value: form.travel_time || 'Unavailable until a driving route is found.' }
        ];
    }, [form.arrival_station, form.departure_station, form.road_distance, form.travel_mode, form.travel_time]);

    const draftCount = registerRows.length;
    const pipelineValue = useMemo(
        () => formatCurrencyInput(registerRows.reduce((sum, row) => {
            const initialFee = Number(String(row.proposed_initial_report_fee || row.initial_fee || '').replace(/[^\d.-]/g, ''));
            const interimFee = Number(String(row.proposed_interim_report_fee || row.interim_fee || '').replace(/[^\d.-]/g, ''));
            return sum + (Number.isFinite(initialFee) ? initialFee : 0) + (Number.isFinite(interimFee) ? interimFee : 0);
        }, 0)),
        [registerRows]
    );
    const canGenerateProposal = Boolean(
        String(form.issue_date || '').trim()
        && String(form.client_name || '').trim()
        && String(form.site_address || '').trim()
        && String(form.service_line || '').trim()
    );

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedSiteAddress(String(form.site_address || '').trim());
        }, 2000);

        return () => {
            window.clearTimeout(handle);
        };
    }, [form.site_address]);

    useEffect(() => {
        function handleUserPreferencesUpdated(event) {
            setReferenceSeed(readReferenceSeed(event?.detail || parseUserPreferencesFromStorage()));
        }

        function handleClientsUpdated(event) {
            const nextClients = Array.isArray(event?.detail) ? event.detail : loadClientsFromStorage();
            setClientOptions(nextClients);
        }

        window.addEventListener('monsurv:user-preferences-updated', handleUserPreferencesUpdated);
        window.addEventListener('monsurv:clients-updated', handleClientsUpdated);
        return () => {
            window.removeEventListener('monsurv:user-preferences-updated', handleUserPreferencesUpdated);
            window.removeEventListener('monsurv:clients-updated', handleClientsUpdated);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function updateTravelDetails() {
            const officeAddress = String(selectedOffice?.address || selectedOffice?.office_location || '').trim();
            const siteAddress = String(debouncedSiteAddress || '').trim();
            const travelMode = form.travel_mode === 'train' ? 'train' : 'driving';

            if (!officeAddress || !siteAddress) {
                setDistanceStatus('Enter an office and project address to calculate travel details.');
                setForm((current) => clearTravelFields(current));
                return;
            }

            setDistanceStatus(travelMode === 'train' ? 'Calculating train journey...' : 'Calculating driving route...');

            try {
                const details = await calculateTravelDetails({
                    originAddress: officeAddress,
                    destinationAddress: siteAddress,
                    travelMode
                });

                if (travelMode === 'train') {
                    const transitSteps = Array.isArray(details.transitSteps) ? details.transitSteps : [];
                    if (!transitSteps.length || transitSteps.some((step) => !isTrainTransitStep(step))) {
                        throw new Error('NON_TRAIN_TRANSIT_SEGMENT');
                    }
                }

                if (cancelled) return;
                setDistanceStatus(travelMode === 'train' ? 'Train travel details updated.' : 'Driving route details updated.');
                setForm((current) => ({
                    ...current,
                    road_distance: details.road_distance,
                    travel_time: details.travel_time,
                    departure_station: details.departure_station,
                    arrival_station: details.arrival_station
                }));
            } catch (e) {
                if (cancelled) return;
                const message = travelMode === 'train'
                    ? 'Train details unavailable until a train-only route is found.'
                    : 'Driving details unavailable until a valid route is found.';
                setDistanceStatus(`${message} ${e?.message || 'Travel calculation failed.'}`);
                setForm((current) => clearTravelFields(current));
            }
        }

        updateTravelDetails();
        return () => {
            cancelled = true;
        };
    }, [debouncedSiteAddress, form.travel_mode, selectedOffice?.address, selectedOffice?.office_location]);

    function handleFieldChange(event) {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: value }));
    }

    function handleCurrencyChange(event) {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: sanitizeCurrencyValue(value) }));
    }

    function handleCurrencyBlur(event) {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: formatCurrencyFieldValue(value) }));
    }

    function handleNumberBlur(event) {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: formatNumberFieldValue(value) }));
    }

    function handleResetDraft() {
        setGenerateError('');
        setGenerateMessage('');
        setForm((current) => ({
            ...createEmptyForm(),
            office_id: current.office_id || officeSettings.primary_office_id || officeSettings.offices[0]?.id || ''
        }));
    }

    function resetFormForNextEntry() {
        setForm((current) => ({
            ...createEmptyForm(),
            office_id: current.office_id || officeSettings.primary_office_id || officeSettings.offices[0]?.id || ''
        }));
    }

    async function handleGenerateProposal() {
        setGenerateError('');
        setGenerateMessage('');
        setGenerating(true);

        try {
            const payload = {
                proposal_reference: proposalReference,
                client_name: form.client_name,
                client_point_of_contact: form.client_point_of_contact,
                client_head_office_address: String(selectedClient?.headOfficeAddress || selectedClient?.head_office_address || '').trim(),
                site_address: form.site_address,
                contract_value: form.contract_value,
                lead_office_id: form.office_id,
                lead_office_name: String(selectedOffice?.office_location || selectedOffice?.address || '').trim(),
                service_line: form.service_line,
                issue_date: form.issue_date,
                travel_mode: form.travel_mode,
                road_distance: form.road_distance,
                travel_time: form.travel_time,
                departure_station: form.departure_station,
                arrival_station: form.arrival_station,
                proposed_initial_report_fee: form.proposed_initial_report_fee,
                proposed_interim_report_fee: form.proposed_interim_report_fee,
                funder_company: form.client_name
            };

            const json = await fetchJson(`${API_BASE_URL}/fee-proposals/generate`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaderOnly(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const created = json?.item || json?.proposal || null;
            if (created?.fee_proposal_id) {
                setRegisterRows((current) => {
                    const filtered = current.filter((row) => row.fee_proposal_id !== created.fee_proposal_id);
                    return [created, ...filtered];
                });
            } else {
                await loadFeeProposalRegister();
            }
            resetFormForNextEntry();
            setGenerateMessage('Fee proposal generated and added to the issued register.');
        } catch (e) {
            setGenerateError(e?.message || 'Failed to generate fee proposal');
        } finally {
            setGenerating(false);
        }
    }

    async function downloadFeeProposal(row, { openDialog = true } = {}) {
        const proposalId = String(row?.fee_proposal_id || '').trim();
        if (!proposalId) return;

        setGenerateError('');
        setGenerateMessage('');
        setDownloadingId(proposalId);
        try {
            const resp = await fetch(`${API_BASE_URL}/fee-proposals/${encodeURIComponent(proposalId)}/download`, {
                method: 'POST',
                headers: getAuthHeaderOnly()
            });
            if (!resp.ok) {
                const json = await resp.json().catch(() => ({}));
                throw new Error(json?.error || `HTTP ${resp.status}`);
            }
            const blob = await resp.blob();
            const fileName = parseContentDispositionFileName(resp.headers.get('Content-Disposition'))
                || `${row?.proposal_reference || 'fee_proposal'}.docx`;
            triggerBrowserDownload(blob, fileName);
            await loadFeeProposalRegister();
            const latestRow = { ...row, status_label: 'Downloaded' };
            if (openDialog) setIssueDialogRow(latestRow);
            setGenerateMessage('Fee proposal downloaded.');
        } catch (e) {
            setGenerateError(e?.message || 'Failed to download fee proposal');
        } finally {
            setDownloadingId('');
        }
    }

    async function markFeeProposalIssued(row) {
        const proposalId = String(row?.fee_proposal_id || '').trim();
        if (!proposalId) return;

        setGenerateError('');
        setGenerateMessage('');
        setIssuingId(proposalId);
        try {
            await fetchJson(`${API_BASE_URL}/fee-proposals/${encodeURIComponent(proposalId)}/issue`, {
                method: 'POST',
                headers: getAuthHeaderOnly()
            });
            await loadFeeProposalRegister();
            setIssueDialogRow(null);
            setGenerateMessage('Fee proposal marked as issued.');
        } catch (e) {
            setGenerateError(e?.message || 'Failed to mark fee proposal as issued');
        } finally {
            setIssuingId('');
        }
    }

    function openRowMenu(event, row) {
        event.preventDefault();
        setRowMenuAnchorEl(event.currentTarget || null);
        setRowMenuRow(row || null);
    }

    function closeRowMenu() {
        setRowMenuAnchorEl(null);
        setRowMenuRow(null);
    }

    function openDeleteDialog(row) {
        closeRowMenu();
        setDeleteDialogRow(row || null);
        setDeletePassword('');
        setDeleteError('');
    }

    function openEditDialog(row) {
        closeRowMenu();
        const currentOfficeId = String(row?.lead_office_id || '').trim();
        const matchingOffice = offices.find((office) => office.id === currentOfficeId);
        setEditDialogRow(row || null);
        setEditForm({
            office_id: matchingOffice?.id || offices[0]?.id || '',
            proposed_initial_report_fee: sanitizeCurrencyValue(row?.proposed_initial_report_fee),
            proposed_interim_report_fee: sanitizeCurrencyValue(row?.proposed_interim_report_fee)
        });
        setEditError('');
    }

    function closeEditDialog() {
        if (editBusy) return;
        setEditDialogRow(null);
        setEditError('');
    }

    async function saveFeeProposalEdit() {
        const proposalId = String(editDialogRow?.fee_proposal_id || '').trim();
        const selectedEditOffice = offices.find((office) => office.id === editForm.office_id) || null;
        if (!proposalId || !selectedEditOffice) {
            setEditError('Select a configured service office.');
            return;
        }

        setEditBusy(true);
        setEditError('');
        setGenerateError('');
        setGenerateMessage('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/fee-proposals/${encodeURIComponent(proposalId)}`, {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaderOnly(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    lead_office_id: selectedEditOffice.id,
                    lead_office_name: selectedEditOffice.office_location || selectedEditOffice.address || selectedEditOffice.id,
                    proposed_initial_report_fee: editForm.proposed_initial_report_fee,
                    proposed_interim_report_fee: editForm.proposed_interim_report_fee
                })
            });
            const updated = json?.proposal || null;
            if (updated?.fee_proposal_id) {
                setRegisterRows((current) => current.map((row) => row.fee_proposal_id === updated.fee_proposal_id ? updated : row));
            } else {
                await loadFeeProposalRegister();
            }
            closeEditDialog();
            setGenerateMessage('Fee proposal updated. Select Download to generate the revised proposal.');
        } catch (e) {
            setEditError(e?.message || 'Failed to update fee proposal');
        } finally {
            setEditBusy(false);
        }
    }

    function canIssueRow(row) {
        const status = String(row?.status_label || '').trim().toLowerCase();
        return Boolean(row?.fee_proposal_id) && status !== 'issued' && status !== 'live';
    }

    function canDownloadRow(row) {
        return Boolean(row?.fee_proposal_id);
    }

    async function handleDownloadFromMenu() {
        const row = rowMenuRow;
        closeRowMenu();
        if (!row) return;
        await downloadFeeProposal(row, { openDialog: true });
    }

    async function handleIssueFromMenu() {
        const row = rowMenuRow;
        closeRowMenu();
        if (!row || !canIssueRow(row)) return;
        await markFeeProposalIssued(row);
    }

    function closeDeleteDialog() {
        if (deleteBusy) return;
        setDeleteDialogRow(null);
        setDeletePassword('');
        setDeleteError('');
    }

    async function confirmDeleteFeeProposal() {
        const proposalId = String(deleteDialogRow?.fee_proposal_id || '').trim();
        const password = String(deletePassword || '').trim();
        if (!proposalId) return;
        if (!password) {
            setDeleteError('Deletion password is required.');
            return;
        }

        setDeleteBusy(true);
        setDeleteError('');
        setGenerateError('');
        setGenerateMessage('');
        try {
            const response = await fetch(`${API_BASE_URL}/fee-proposals/${encodeURIComponent(proposalId)}`, {
                method: 'DELETE',
                headers: {
                    ...getAuthHeaderOnly(),
                    'X-Deletion-Password': password
                }
            });
            if (!response.ok) {
                const json = await response.json().catch(() => ({}));
                throw new Error(json?.error || `HTTP ${response.status}`);
            }

            setRegisterRows((current) => current.filter((row) => row.fee_proposal_id !== proposalId));
            setIssueDialogRow((current) => (current?.fee_proposal_id === proposalId ? null : current));
            closeDeleteDialog();
            setGenerateMessage('Fee proposal deleted.');
        } catch (e) {
            setDeleteError(e?.message || 'Failed to delete fee proposal');
        } finally {
            setDeleteBusy(false);
        }
    }

    function getStatusChipProps(row) {
        const status = String(row?.status_label || '').trim();
        if (status === 'Live') return { color: 'secondary', variant: 'filled' };
        if (status === 'Issued') return { color: 'primary', variant: 'filled' };
        if (status === 'Downloaded') return { color: 'success', variant: 'filled' };
        if (status === 'Letter Generated') return { color: 'default', variant: 'outlined' };
        return { color: 'default', variant: 'outlined' };
    }

    return (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
            <AnalyticsPageHero
                title="Fee proposals and commercial pipeline"
                chips={[
                    'Pipeline ready',
                    `Owner: ${displayName}`,
                    selectedOffice?.office_location ? `Lead office: ${selectedOffice.office_location}` : 'Office map linked',
                ]}
                actions={(
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" disableElevation onClick={handleResetDraft}>
                            Reset draft
                        </Button>
                        <Button variant="contained" startIcon={<RequestPageIcon />} disableElevation onClick={handleGenerateProposal} disabled={generating || !canGenerateProposal}>
                            {generating ? 'Generating…' : 'Create proposal'}
                        </Button>
                    </Stack>
                )}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <AnalyticsMetricCard
                    icon={<DescriptionOutlinedIcon />}
                    label="Issued proposals"
                    value={String(draftCount)}
                    caption="This register is empty until live fee proposals are created."
                    accent="linear-gradient(135deg, rgba(14, 116, 144, 0.96), rgba(34, 211, 238, 0.58))"
                />
                <AnalyticsMetricCard
                    icon={<TrendingUpIcon />}
                    label="Pipeline value"
                    value={pipelineValue}
                    caption="No values are shown until real fee proposals exist."
                    accent="linear-gradient(135deg, rgba(21, 128, 61, 0.96), rgba(74, 222, 128, 0.56))"
                />
            </Stack>

            <Grid container spacing={2.25} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.25,
                            height: '100%',
                            minHeight: 620,
                            borderRadius: 4,
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(246,249,252,0.98) 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <Stack spacing={0.6}>
                            <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 800 }}>
                                Proposal Brief
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Fee proposal form scaffold
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This form drives the generated fee proposal using the template assigned to the selected service line.
                            </Typography>
                        </Stack>

                        {generateError ? <Alert severity="error">{generateError}</Alert> : null}
                        {generateMessage ? <Alert severity="success">{generateMessage}</Alert> : null}
                        {registerError ? <Alert severity="error">{registerError}</Alert> : null}

                        <Grid container spacing={1.4}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth label="Issue date" name="issue_date" type="date" value={form.issue_date} onChange={handleFieldChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Client"
                                    name="client_name"
                                    value={form.client_name}
                                    onChange={handleFieldChange}
                                    helperText={clientCompanyNames.length ? 'Company names sourced from Clients.' : 'Add companies in Clients to populate this dropdown.'}
                                >
                                    <MenuItem value="">Select client</MenuItem>
                                    {clientCompanyNames.map((companyName) => (
                                        <MenuItem key={companyName} value={companyName}>{companyName}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid key="proposal-reference-field" size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Proposal Reference"
                                    value={proposalReference}
                                    InputProps={{ readOnly: true }}
                                    helperText="Set the number in Settings > Reports > Report Defaults."
                                />
                            </Grid>
                            <Grid key="client-point-of-contact-field" size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Client Point of Contact"
                                    name="client_point_of_contact"
                                    value={form.client_point_of_contact}
                                    onChange={handleFieldChange}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField fullWidth multiline minRows={3} label="Project Address" name="site_address" value={form.site_address} onChange={handleFieldChange} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Contract Value"
                                    name="contract_value"
                                    value={form.contract_value}
                                    onChange={handleCurrencyChange}
                                    onBlur={handleCurrencyBlur}
                                    InputProps={{ startAdornment: <InputAdornment position="start">GBP</InputAdornment> }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select fullWidth label="Lead office" name="office_id" value={form.office_id} onChange={handleFieldChange} helperText={offices.length ? 'Loaded from Office Map settings' : 'Configure offices in Settings to populate this list'}>
                                    {offices.length ? offices.map((office) => (
                                        <MenuItem key={office.id} value={office.id}>{office.office_location || office.address || office.id}</MenuItem>
                                    )) : <MenuItem value="">No configured offices</MenuItem>}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select fullWidth label="Service line" name="service_line" value={form.service_line} onChange={handleFieldChange}>
                                    <MenuItem value="">Select service line</MenuItem>
                                    {FEE_PROPOSAL_SERVICE_LINE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.75,
                                borderRadius: 3,
                                border: '1px solid rgba(148, 163, 184, 0.18)',
                                background: 'rgba(255,255,255,0.72)',
                                display: 'grid',
                                gap: 1.4
                            }}
                        >
                            <Stack spacing={0.35}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Fee basis
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Enter the fee structure and review travel details calculated from the selected office to the project address.
                                </Typography>
                            </Stack>

                            <Grid container spacing={1.4}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Proposed Initial Report Fee"
                                        name="proposed_initial_report_fee"
                                        value={form.proposed_initial_report_fee}
                                        onChange={handleCurrencyChange}
                                        onBlur={handleNumberBlur}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Proposed Interim Report Fee"
                                        name="proposed_interim_report_fee"
                                        value={form.proposed_interim_report_fee}
                                        onChange={handleCurrencyChange}
                                        onBlur={handleNumberBlur}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Travel"
                                        name="travel_mode"
                                        value={form.travel_mode}
                                        onChange={handleFieldChange}
                                        helperText="Controls which travel details are shown."
                                    >
                                        <MenuItem value="driving">Driving</MenuItem>
                                        <MenuItem value="train">Train</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            borderRadius: 2.5,
                                            border: '1px solid rgba(148, 163, 184, 0.22)',
                                            background: 'rgba(248, 250, 252, 0.88)',
                                            px: 1.25,
                                            py: 1.1,
                                            display: 'grid',
                                            gap: 0.85
                                        }}
                                    >
                                        {travelDetailItems.map((item) => (
                                            <Box
                                                key={item.label}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                                                    gap: 0.6,
                                                    alignItems: 'start'
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                                                    {item.label}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                        <Typography variant="caption" color="text.secondary">
                                            {distanceStatus || 'Travel details will populate automatically.'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Divider />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label="Reference auto-generated" variant="outlined" />
                                <Chip size="small" label="Travel details auto-calculated" variant="outlined" />
                                <Chip size="small" label="Uses service-line template" variant="outlined" />
                            </Stack>
                            <Button variant="contained" disableElevation onClick={handleGenerateProposal} disabled={generating || !canGenerateProposal}>
                                {generating ? 'Generating…' : 'Generate Fee Letter'}
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.25,
                            height: '100%',
                            minHeight: 620,
                            borderRadius: 4,
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(246,249,252,0.98) 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                            <Box>
                                <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 800 }}>
                                    Office Coverage
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    UK office map
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configured office locations are shown as consistent pins. This card is already linked to the dashboard office-map settings.
                                </Typography>
                            </Box>
                            <Chip icon={<PlaceOutlinedIcon />} label={`${offices.length} office${offices.length === 1 ? '' : 's'}`} variant="outlined" />
                        </Stack>

                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 520,
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: '1px solid rgba(148, 163, 184, 0.18)',
                                background: 'rgba(255,255,255,0.7)'
                            }}
                        >
                            <UKProjectMap
                                projects={mapProjects}
                                officeSettings={officeSettings}
                                showDirections
                                routeMode={form.travel_mode === 'train' ? 'transit' : 'driving'}
                                fillHeight
                                minHeight={520}
                            />
                        </Box>

                        <Stack spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Configured offices
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {offices.length ? offices.map((office) => (
                                    <Chip
                                        key={office.id}
                                        size="small"
                                        label={office.office_location || office.address || office.id}
                                        sx={{
                                            borderColor: office.pin_color,
                                            color: office.pin_color,
                                            '& .MuiChip-outlined': { borderColor: office.pin_color }
                                        }}
                                        variant="outlined"
                                    />
                                )) : <Chip size="small" label="No offices configured yet" variant="outlined" />}
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.25,
                            borderRadius: 4,
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(246,249,252,0.98) 100%)',
                            display: 'grid',
                            gap: 1.5
                        }}
                    >
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                            <Box>
                                <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 800 }}>
                                    Issued Register
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Issued fee proposals
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Generated fee proposals appear here first as Letter Generated, then move through Downloaded and Issued. Right-click a row to delete it.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" onClick={loadFeeProposalRegister} disabled={registerLoading || downloadingId || issuingId || deleteBusy}>
                                    {registerLoading ? 'Refreshing…' : 'Refresh register'}
                                </Button>
                            </Stack>
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>Reference</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Client</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Project</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Office</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Issued</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Initial Fee</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Interim Fee</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 800, textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {registerRows.length ? registerRows.map((row) => (
                                        <TableRow key={row.fee_proposal_id} hover onContextMenu={(event) => openRowMenu(event, row)}>
                                            <TableCell sx={{ fontWeight: 700 }}>{row.proposal_reference || '-'}</TableCell>
                                            <TableCell>{row.client_name || '-'}</TableCell>
                                            <TableCell>{row.project_address || '-'}</TableCell>
                                            <TableCell>{row.lead_office_name || '-'}</TableCell>
                                            <TableCell>{row.issue_date || '-'}</TableCell>
                                            <TableCell>{row.proposed_initial_report_fee || '-'}</TableCell>
                                            <TableCell>{row.proposed_interim_report_fee || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.status_label}
                                                    {...getStatusChipProps(row)}
                                                    onClick={row.status_label === 'Issued' || row.status_label === 'Live' ? undefined : () => downloadFeeProposal(row)}
                                                    onDelete={row.status_label === 'Issued' || row.status_label === 'Live' ? undefined : () => downloadFeeProposal(row)}
                                                    deleteIcon={<DownloadRoundedIcon />}
                                                    sx={row.status_label === 'Issued' || row.status_label === 'Live' ? undefined : { cursor: 'pointer' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => openRowMenu(event, row)}
                                                    aria-label={`Actions for ${row.proposal_reference || 'fee proposal'}`}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                                {registerLoading ? 'Loading fee proposals…' : 'No issued fee proposals yet.'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={Boolean(issueDialogRow)} onClose={() => setIssueDialogRow(null)} fullWidth maxWidth="xs">
                <DialogTitle>Fee Proposal Issued?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Has fee proposal {issueDialogRow?.proposal_reference ? `${issueDialogRow.proposal_reference} ` : ''}been issued to the client?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIssueDialogRow(null)} disabled={Boolean(downloadingId || issuingId)}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => issueDialogRow && downloadFeeProposal(issueDialogRow, { openDialog: true })}
                        disabled={Boolean(downloadingId || issuingId)}
                    >
                        {downloadingId && issueDialogRow?.fee_proposal_id === downloadingId ? 'Downloading…' : 'Download Again'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => issueDialogRow && markFeeProposalIssued(issueDialogRow)}
                        disabled={Boolean(downloadingId || issuingId)}
                    >
                        {issuingId && issueDialogRow?.fee_proposal_id === issuingId ? 'Updating…' : 'Issued to Client'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={rowMenuAnchorEl}
                open={Boolean(rowMenuAnchorEl && rowMenuRow)}
                onClose={closeRowMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => openEditDialog(rowMenuRow)} disabled={deleteBusy || Boolean(downloadingId || issuingId || editBusy)}>
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDownloadFromMenu} disabled={deleteBusy || Boolean(downloadingId || issuingId) || !canDownloadRow(rowMenuRow)}>
                    Download
                </MenuItem>
                <MenuItem onClick={handleIssueFromMenu} disabled={deleteBusy || Boolean(downloadingId || issuingId) || !canIssueRow(rowMenuRow)}>
                    Mark issued
                </MenuItem>
                <MenuItem onClick={() => openDeleteDialog(rowMenuRow)} disabled={deleteBusy || Boolean(downloadingId || issuingId)}>
                    Delete row
                </MenuItem>
            </Menu>

            <Dialog open={Boolean(editDialogRow)} onClose={closeEditDialog} fullWidth maxWidth="xs">
                <DialogTitle>Edit Fee Proposal</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Saving changes returns this proposal to Download. Downloading it will generate a revised fee proposal.
                    </DialogContentText>
                    {editError ? <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert> : null}
                    <Stack spacing={1.5}>
                        <TextField
                            select
                            fullWidth
                            label="Service Office"
                            value={editForm.office_id}
                            onChange={(event) => setEditForm((current) => ({ ...current, office_id: event.target.value }))}
                            disabled={editBusy || !offices.length}
                            helperText={offices.length ? 'Loaded from Office Map settings.' : 'Configure a service office in Settings first.'}
                        >
                            {offices.length ? offices.map((office) => (
                                <MenuItem key={office.id} value={office.id}>{office.office_location || office.address || office.id}</MenuItem>
                            )) : <MenuItem value="">No configured offices</MenuItem>}
                        </TextField>
                        <TextField
                            fullWidth
                            label="Initial Fee"
                            value={editForm.proposed_initial_report_fee}
                            onChange={(event) => setEditForm((current) => ({ ...current, proposed_initial_report_fee: sanitizeCurrencyValue(event.target.value) }))}
                            InputProps={{ startAdornment: <InputAdornment position="start">GBP</InputAdornment> }}
                            disabled={editBusy}
                        />
                        <TextField
                            fullWidth
                            label="Interim Fee"
                            value={editForm.proposed_interim_report_fee}
                            onChange={(event) => setEditForm((current) => ({ ...current, proposed_interim_report_fee: sanitizeCurrencyValue(event.target.value) }))}
                            InputProps={{ startAdornment: <InputAdornment position="start">GBP</InputAdornment> }}
                            disabled={editBusy}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog} disabled={editBusy}>Cancel</Button>
                    <Button variant="contained" onClick={saveFeeProposalEdit} disabled={editBusy || !editForm.office_id}>
                        {editBusy ? 'Saving...' : 'Save changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteDialogRow)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Delete Fee Proposal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Delete {deleteDialogRow?.proposal_reference ? `${deleteDialogRow.proposal_reference} ` : 'this fee proposal '}from the register and remove its generated letter from storage.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        fullWidth
                        type="password"
                        label="Deletion Password"
                        value={deletePassword}
                        onChange={(event) => setDeletePassword(event.target.value)}
                        error={Boolean(deleteError)}
                        helperText={deleteError || 'Enter the admin deletion password to confirm.'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteBusy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDeleteFeeProposal} disabled={deleteBusy || !String(deletePassword || '').trim()}>
                        {deleteBusy ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function MANAGEMENT_DEFAULT_OFFICE(currentOfficeId, settings) {
    const offices = Array.isArray(settings?.offices) ? settings.offices : [];
    if (currentOfficeId && offices.some((office) => office.id === currentOfficeId)) return currentOfficeId;
    return settings?.primary_office_id || offices[0]?.id || '';
}